import { useCallback, useEffect, useState } from "react";
import { checkSession } from "../../api/sessionCheck";
import {
  getUserId,
  getUserLanguage,
  setSessionUserRole,
  setUserLanguage,
} from "../../api/sessionManagement";
import { useTranslation } from "react-i18next";
import { updateUserLanguageCache } from "../../hooks/useUserLanguage.hook";
import { useAppDispatch } from "../../hooks/ReduxHooks";
import {
  setReservationAllowance,
  setReservationAllowanceLoading,
  setReservationAllowanceError,
} from "../../store/slices/ReservationAllowanceSlice";
import { fetchReservationAllowance } from "../../api/reservationAllowance";

export const useSession = () => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const { i18n } = useTranslation();
  const dispatch = useAppDispatch();

  const checkReservationAllowance = useCallback(async () => {
    const sessionId = localStorage.getItem("sessionId");

    if (!sessionId) {
      dispatch(setReservationAllowance(false));
      return;
    }

    dispatch(setReservationAllowanceLoading(true));
    try {
      const response = await fetchReservationAllowance();
      dispatch(setReservationAllowance(response.is_allow_reservation));
    } catch (error) {
      dispatch(
        setReservationAllowanceError("Failed to check reservation allowance")
      );
      dispatch(setReservationAllowance(false));
    }
  }, [dispatch]);

  const checkUserSession = useCallback(async () => {
    const sessionId = localStorage.getItem("sessionId");

    if (!sessionId) {
      setIsAuthenticated(false);
      dispatch(setReservationAllowance(false));
      return;
    }

    try {
      const response = await checkSession();
      if (!response.error) {
        const roleFromApi = response?.data?.data?.role;
        if (roleFromApi) {
          setSessionUserRole(roleFromApi);
        }
        setIsAuthenticated(true);
        const langFromApi = response?.data?.data?.lang;

        if (langFromApi) {
          const userId = getUserId();
          if (userId) {
            const localLang = getUserLanguage(userId);
            if (localLang !== langFromApi) {
              setUserLanguage(userId, langFromApi);
              updateUserLanguageCache(userId, langFromApi);
              await i18n.changeLanguage(langFromApi);
            }
          }
        }

        await checkReservationAllowance();
      } else {
        localStorage.removeItem("sessionId");
        setIsAuthenticated(false);
        dispatch(setReservationAllowance(false));
      }
    } catch (error) {
      localStorage.removeItem("sessionId");
      setIsAuthenticated(false);
      dispatch(setReservationAllowance(false));
    }
  }, [i18n, checkReservationAllowance, dispatch]);

  useEffect(() => {
    checkUserSession();
  }, [checkUserSession]);

  return { isAuthenticated, checkUserSession };
};
